import type { ManeuverKind } from '@konvoi/shared';

/**
 * Die Zeichen der Abbiegehinweise.
 *
 * Gezeichnet und nicht aus der Schrift geholt — aus demselben Grund wie die
 * Zustands- und Meldungszeichen: Pfeilzeichen führt nicht jede Systemschrift,
 * und ein leeres Kästchen an der wichtigsten Stelle des Bildschirms wäre das
 * schlechteste Ergebnis.
 *
 * Sieben Bilder für dreizehn Arten. Leicht, normal und scharf teilen sich ein
 * Zeichen: Im Vorbeischauen sind drei Abstufungen desselben Pfeils nicht
 * auseinanderzuhalten, und wie scharf es wird, steht daneben in Worten. Das
 * Zeichen beantwortet die eine Frage, die im Auto zählt — **welche Seite**.
 *
 * Groß, kräftig, einfarbig: Es steht bei Sonne in der Halterung und wird aus
 * einem Meter Abstand im Vorbeifahren gelesen.
 */
const STRICH = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.4,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/**
 * Ansage ein oder aus.
 *
 * Zwei Bilder, die sich schon an der Silhouette unterscheiden — mit Wellen und
 * durchgestrichen. Ein Lautsprecher, der sich nur in der Farbe ändert, wäre bei
 * Sonne auf dem Display nicht zu unterscheiden.
 */
export function ZeichenAnsage({ an }: { an: boolean }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="26"
      height="26"
      aria-hidden="true"
      focusable="false"
    >
      <path {...STRICH} d="M4 9 H8 L13 5 V19 L8 15 H4 Z" />
      {an ? (
        <>
          <path {...STRICH} d="M16.5 9.5 A4 4 0 0 1 16.5 14.5" />
          <path {...STRICH} d="M19 7 A7.5 7.5 0 0 1 19 17" />
        </>
      ) : (
        <path {...STRICH} d="M17 9.5 L22 14.5 M22 9.5 L17 14.5" />
      )}
    </svg>
  );
}

export function ZeichenManöver({ kind }: { kind: ManeuverKind }): React.JSX.Element {
  return (
    <svg
      className="manoeverzeichen"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      {pfad(kind)}
    </svg>
  );
}

function pfad(kind: ManeuverKind): React.JSX.Element {
  switch (kind) {
    case 'left':
    case 'slight_left':
    case 'sharp_left':
      return (
        <>
          <path {...STRICH} d="M12 22 V11 H6" />
          <path {...STRICH} d="M10 7 L6 11 L10 15" />
        </>
      );

    case 'right':
    case 'slight_right':
    case 'sharp_right':
      return (
        <>
          <path {...STRICH} d="M12 22 V11 H18" />
          <path {...STRICH} d="M14 7 L18 11 L14 15" />
        </>
      );

    case 'uturn':
      return (
        <>
          {/* Hoch, oben herum, und auf der anderen Seite wieder hinunter. */}
          <path {...STRICH} d="M16 22 V11 A4 4 0 0 0 8 11 V17" />
          <path {...STRICH} d="M5 14 L8 17 L11 14" />
        </>
      );

    case 'roundabout':
      return (
        <>
          <circle {...STRICH} cx="12" cy="12" r="5" />
          <path {...STRICH} d="M12 22 V17" />
          <path {...STRICH} d="M17 12 H22" />
          <path {...STRICH} d="M19 9 L22 12 L19 15" />
        </>
      );

    case 'merge':
      return (
        <>
          {/* Zwei Fahrbahnen, die zu einer werden. */}
          <path {...STRICH} d="M12 22 V12" />
          <path {...STRICH} d="M6 20 C6 15 12 15 12 12" />
          <path {...STRICH} d="M12 12 V4" />
          <path {...STRICH} d="M8 8 L12 4 L16 8" />
        </>
      );

    case 'ferry':
      return (
        <>
          {/* Rumpf über Wasser — kein Pfeil, weil es hier nichts zu lenken gibt. */}
          <path {...STRICH} d="M4 14 H20 L17 19 H7 Z" />
          <path {...STRICH} d="M12 14 V7" />
          <path {...STRICH} d="M3 21 C5 19.5 7 22.5 9 21 C11 19.5 13 22.5 15 21 C17 19.5 19 22.5 21 21" />
        </>
      );

    case 'destination':
      return (
        <>
          {/* Die Fahne am Ziel — und das Ziel ist die Kolonne. */}
          <path {...STRICH} d="M7 22 V3" />
          <path {...STRICH} d="M7 4 H18 L15 8.5 L18 13 H7" />
        </>
      );

    case 'start':
    case 'straight':
      return (
        <>
          <path {...STRICH} d="M12 22 V4" />
          <path {...STRICH} d="M7 9 L12 4 L17 9" />
        </>
      );
  }
}
